import React, { useEffect, useRef, useState, useCallback } from 'react'
import isEmpty from 'lodash/isEmpty'
import { InputGroup } from 'react-bootstrap'
import type { GlobalSearchResult } from '@ztechapps/common.contract'
import { getWindow } from '@ztechapps/common.utils'
import { BusyButton } from '@ztechapps/webui.button'
import { TextInput } from '@ztechapps/webui.input'
import { ShowIf, useKeyboardShortcutContext } from '@ztechapps/webui.util'
import { useGlobalSearch } from './useGlobalSearch'
import { GlobalSearchResultsDropdown } from './GlobalSearchResultsDropdown'

export type GlobalSearchBarProps = {
  onResultClick: (result: GlobalSearchResult) => void
  placeholder?: string
  /** Group headings by result module; a module with no entry prints its raw name. */
  moduleLabels?: Record<string, string>
}

export const GlobalSearchBar = ({
  onResultClick,
  placeholder = 'Search orders, projects, customers...',
  moduleLabels,
}: GlobalSearchBarProps) => {
  const { searchQuery, setSearchQuery, handleSearch, results, loading, error, canSearch, clearSearch } =
    useGlobalSearch()

  const [showDropdown, setShowDropdown] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Detect if user is on Mac to show correct keyboard shortcut
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform)
  const keyboardShortcut = isMac ? '⌘K' : 'Ctrl+K'

  // Without a KeyboardShortcutProvider the context value is `{}`; with one, the app's rebindable Focus search
  // shortcut owns the key, so this hard-coded listener would keep answering to Ctrl/Cmd+K after a rebind.
  const hasShortcutProvider = !!useKeyboardShortcutContext().getResolvedKeys

  // Handle Cmd/Ctrl + K keyboard shortcut
  useEffect(() => {
    if (hasShortcutProvider) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        inputRef.current?.focus()
      }
    }

    const window = getWindow()
    window.document.addEventListener('keydown', handleKeyDown)

    return () => {
      window.document.removeEventListener('keydown', handleKeyDown)
    }
  }, [hasShortcutProvider])

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowDropdown(false)
        setIsFocused(false)
      }
    }

    const window = getWindow()
    window.document.addEventListener('mousedown', handleClickOutside)

    return () => {
      window.document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Show dropdown when results are available or when loading
  useEffect(() => {
    if (loading || results || error) {
      setShowDropdown(true)
    }
  }, [loading, results, error])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (canSearch) {
        handleSearch()
      }
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setShowDropdown(false)
      inputRef.current?.blur()
    }
  }

  const handleSearchClick = () => {
    if (canSearch) {
      handleSearch()
    }
  }

  const handleResultClick = useCallback(
    (result: GlobalSearchResult) => {
      setShowDropdown(false)
      setIsFocused(false)
      onResultClick(result)
    },
    [onResultClick]
  )

  const handleFocus = () => {
    setIsFocused(true)
    if (results || loading || error) {
      setShowDropdown(true)
    }
  }

  const handleCloseDropdown = () => {
    setShowDropdown(false)
  }

  return (
    <div ref={containerRef} className="position-relative" style={{ minWidth: '300px', maxWidth: '500px' }}>
      <InputGroup>
        <TextInput
          inputRef={inputRef}
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder}
          style={{ paddingRight: '80px' }}
        />
        <div className="position-absolute end-0 top-0 h-100 d-flex align-items-center pe-2" style={{ zIndex: 10 }}>
          {!isEmpty(searchQuery) && (
            <BusyButton
              variant="link"
              bsSize="sm"
              icon="times"
              onClick={() => {
                clearSearch()
                inputRef.current?.focus()
              }}
              className="text-muted p-1 me-1"
              title="Clear search"
            />
          )}
          <BusyButton
            variant="link"
            bsSize="sm"
            icon="search"
            onClick={handleSearchClick}
            disabled={!canSearch}
            isBusy={loading}
            busyText=" "
            className={`p-1 ${canSearch ? 'text-primary' : 'text-muted'}`}
            title={canSearch ? 'Search (Enter)' : 'Type at least 3 characters'}
          />
        </div>
      </InputGroup>

      {/* The hint names this bar's own hard-coded key; with a provider mounted that listener is off. */}
      <ShowIf condition={!hasShortcutProvider && !isFocused && isEmpty(searchQuery)}>
        <div
          className="position-absolute end-0 top-50 translate-middle-y pe-5 text-muted small"
          style={{ pointerEvents: 'none' }}
        >
          <kbd className="bg-body-tertiary text-body border px-1">{keyboardShortcut}</kbd>
        </div>
      </ShowIf>

      <GlobalSearchResultsDropdown
        results={results}
        loading={loading}
        error={error}
        show={showDropdown}
        onResultClick={handleResultClick}
        onClose={handleCloseDropdown}
        searchQuery={searchQuery}
        moduleLabels={moduleLabels}
      />
    </div>
  )
}
